import { SessionExpiredError } from '@/lib/session-error';
import { z } from 'zod';
import { apiFetch } from '@/lib/api-fetch';
import type { AssignmentGroupInput } from '@/features/reference-data/data/http-workspace';
import type { GlobalRole } from '@opsflow/api-contracts';
export type { GlobalRole } from '@opsflow/api-contracts';
import { apiErrorMessage, apiFieldErrors } from '@/lib/api-errors';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { ReferenceDataError } from '@/features/reference-data/data/admin-reference-data-source';
import * as m from '@/paraglide/messages.js';

// Mirrors apps/server/src/users/types.ts.

export type PersonStatus = 'ACTIVE' | 'INACTIVE';
type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';

export interface InvitationView {
  id: string;
  intendedEmail: string;
  status: InvitationStatus;
  issuedAt: string;
  expiresAt: string;
}

interface AccessSummary {
  /** Null when nobody has linked an identity: the person exists but cannot sign in. */
  provider: 'GOOGLE' | 'EMAIL_PASSWORD' | null;
  invitation: InvitationView | null;
  activeSessions: number;
  lastSignInAt: string | null;
}

export interface UserRecord {
  id: string;
  displayName: string;
  emailHint: string | null;
  globalRole: GlobalRole;
  status: PersonStatus;
  version: number;
  access: AccessSummary;
}

export interface CreatedUserRecord extends UserRecord {
  invitationSent: boolean | null;
}

export type ReminderChannels = 'EMAIL' | 'SLACK' | 'BOTH';

export interface ReminderPreference {
  personId: string;
  channels: ReminderChannels;
  version: number;
}

export function readMyReminderPreference(signal?: AbortSignal) {
  return read<ReminderPreference>('/api/me/reminder-preference', signal);
}

export function readReminderPreference(personId: string, signal?: AbortSignal) {
  return read<ReminderPreference>(
    `/api/users/${encodeURIComponent(personId)}/reminder-preference`,
    signal,
  );
}

export function updateMyReminderPreference(
  input: Pick<ReminderPreference, 'channels' | 'version'>,
) {
  return command<ReminderPreference>('/api/me/reminder-preference', input, 'PUT');
}

export function updateReminderPreference(
  personId: string,
  input: Pick<ReminderPreference, 'channels' | 'version'>,
) {
  return command<ReminderPreference>(
    `/api/users/${encodeURIComponent(personId)}/reminder-preference`,
    input,
    'PUT',
  );
}

export interface RoleChange {
  at: string;
  priorRole: GlobalRole | null;
  resultingRole: GlobalRole;
  actorName: string | null;
  reason: string | null;
}

export interface SessionRecord {
  id: string;
  createdAt: string;
  lastActiveAt: string;
  absoluteExpiresAt: string;
  userAgentHint: string | null;
  ipHint: string | null;
  current: boolean;
}

export interface UserFilter {
  query?: string;
  status?: PersonStatus;
  role?: GlobalRole;
}

/** One line of the consolidated people directory. Mirrors the server's DirectoryRow. */
export interface DirectoryRow {
  id: string;
  displayName: string;
  emailHint: string | null;
  globalRole: GlobalRole;
  status: PersonStatus;
  access: {
    provider: 'GOOGLE' | 'EMAIL_PASSWORD' | null;
    lastSignInAt: string | null;
    activeSessions: number;
    pendingInvitation: boolean;
  };
  projects: {
    id: string;
    name: string;
    clientId: string;
    clientName: string;
    state: 'ACTIVE' | 'SCHEDULED';
  }[];
  projectCounts: { active: number; scheduled: number };
  activityCounts: { active: number; scheduled: number };
}

export interface DirectoryFilter extends UserFilter {
  businessDate?: string;
  cursor?: string;
  limit?: number;
}

export interface DirectoryPage {
  items: DirectoryRow[];
  nextCursor: string | null;
  total: number;
  businessDate: string;
}

export const directoryKeys = {
  all: ['directory'] as const,
};

export const directoryQueries = {
  page: (filter: DirectoryFilter) =>
    queryOptions({
      queryKey: [...directoryKeys.all, filter] as const,
      placeholderData: keepPreviousData,
      queryFn: ({ signal }) => {
        const parameters = new URLSearchParams();
        for (const [key, value] of Object.entries(filter)) {
          if (value !== undefined && value !== '') parameters.set(key, String(value));
        }
        const query = parameters.toString();
        return read<DirectoryPage>(`/api/directory${query ? `?${query}` : ''}`, signal);
      },
    }),
};

const rootKey = ['users'] as const;

export const userKeys = {
  all: rootKey,
  lists: () => [...rootKey, 'list'] as const,
  list: (filter: UserFilter) => [...userKeys.lists(), filter] as const,
  detail: (id: string) => [...rootKey, 'detail', id] as const,
  reminderPreference: (id: string) => [...rootKey, 'reminder-preference', id] as const,
  roleHistory: (id: string) => [...rootKey, 'role-history', id] as const,
  sessions: (id: string) => [...rootKey, 'sessions', id] as const,
  deletion: (id: string) => [...rootKey, 'deletion', id] as const,
};

/** What keeps a person from being deleted; mirrors the server's hold codes. */
export type DeletionHold = 'TIMESHEET' | 'IMPORTED_TIME' | 'SUPPLIER' | 'LOGIN';

export const userQueries = {
  list: (filter: UserFilter = {}) =>
    queryOptions({
      placeholderData: keepPreviousData,
      queryKey: userKeys.list(filter),
      queryFn: ({ signal }) => listUsers(filter, signal),
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: userKeys.detail(id),
      queryFn: ({ signal }) => fetchUser(id, signal),
    }),
  deletion: (id: string) =>
    queryOptions({
      queryKey: userKeys.deletion(id),
      queryFn: ({ signal }) =>
        read<{ holds: DeletionHold[] }>(`/api/users/${encodeURIComponent(id)}/deletion`, signal),
    }),
  roleHistory: (id: string) =>
    queryOptions({
      queryKey: userKeys.roleHistory(id),
      queryFn: ({ signal }) =>
        read<RoleChange[]>(`/api/users/${encodeURIComponent(id)}/role-history`, signal),
    }),
  sessions: (id: string) =>
    queryOptions({
      queryKey: userKeys.sessions(id),
      queryFn: ({ signal }) =>
        read<SessionRecord[]>(`/api/users/${encodeURIComponent(id)}/sessions`, signal),
    }),
};

export function fetchUser(id: string, signal?: AbortSignal) {
  return read<UserRecord>(`/api/users/${encodeURIComponent(id)}`, signal);
}

function listUsers(filter: UserFilter = {}, signal?: AbortSignal) {
  const parameters = new URLSearchParams();
  if (filter.query) parameters.set('query', filter.query);
  if (filter.status) parameters.set('status', filter.status);
  if (filter.role) parameters.set('role', filter.role);
  const query = parameters.toString();
  return read<UserRecord[]>(`/api/users${query ? `?${query}` : ''}`, signal);
}

export function createUser(input: {
  displayName: string;
  emailHint?: string | null;
  role?: GlobalRole;
  teamPersonIds?: string[];
  assignments?: { clientId: string; groups: AssignmentGroupInput[] }[];
}) {
  return command<CreatedUserRecord>('/api/users', input);
}

export function updateUser(
  id: string,
  input: { version: number; displayName?: string; emailHint?: string | null },
) {
  return command<UserRecord>(`/api/users/${encodeURIComponent(id)}`, input, 'PATCH');
}

/** `confirmed` is part of the server contract, not a UI flag: FR-012 wants a deliberate act. */
export function assignRole(
  id: string,
  input: { version: number; role: GlobalRole; teamPersonIds?: string[] },
) {
  return command<UserRecord>(
    `/api/users/${encodeURIComponent(id)}/role`,
    { ...input, confirmed: true },
    'PUT',
  );
}

export function setUserStatus(id: string, input: { version: number; status: PersonStatus }) {
  const path = input.status === 'ACTIVE' ? 'activation' : 'deactivation';
  return command<UserRecord>(`/api/users/${encodeURIComponent(id)}/${path}`, {
    version: input.version,
    confirmed: true,
  });
}

export function deleteUser(id: string, version: number) {
  return command<null>(`/api/users/${encodeURIComponent(id)}/deletion`, {
    version,
    confirmed: true,
  });
}

export function inviteUser(id: string, intendedEmail: string) {
  return command<InvitationView>(`/api/users/${encodeURIComponent(id)}/invitation`, {
    intendedEmail,
  });
}

export function revokeInvitation(id: string, invitationId: string) {
  return remove<UserRecord>(
    `/api/users/${encodeURIComponent(id)}/invitation/${encodeURIComponent(invitationId)}`,
  );
}

export function revokeSessions(id: string, sessionId?: string) {
  const base = `/api/users/${encodeURIComponent(id)}/sessions`;
  return remove<{ revoked: number }>(sessionId ? `${base}/${encodeURIComponent(sessionId)}` : base);
}

// ---- Unauthenticated entry points ----------------------------------------------------

export function signInWithPassword(email: string, password: string) {
  return command<{ signedIn: true }>('/api/auth/sessions', { email, password });
}

export function acceptInvitation(token: string, password: string) {
  return command<{ signedIn: true }>('/api/auth/invitation-acceptance', { token, password });
}

export function requestPasswordReset(email: string) {
  return command<null>('/api/auth/password-reset-requests', { email });
}

export function applyPasswordReset(token: string, password: string) {
  return command<null>('/api/auth/password-resets', { token, password });
}

function command<T>(path: string, input: object, method: 'POST' | 'PATCH' | 'PUT' = 'POST') {
  return request<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

function remove<T>(path: string) {
  return request<T>(path, { method: 'DELETE' });
}

function read<T>(path: string, signal?: AbortSignal) {
  return request<T>(path, signal ? { signal } : undefined);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await apiFetch(path, { credentials: 'same-origin', ...init });
  } catch (error) {
    if (error instanceof SessionExpiredError) throw error;
    throw new ReferenceDataError('UNAVAILABLE', m.reference_service_unreachable());
  }
  const body = (await response.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!response.ok || !body || !body.success) throw apiError(body, path, init?.method);
  return body.data;
}

const MESSAGES: Record<string, () => string> = {
  INVALID_CREDENTIALS: m.error_invalid_credentials,
  THROTTLED: m.error_throttled,
  NOT_FOUND: m.error_person_missing,
  INVITATION_EXPIRED: m.error_invitation_expired,
  INVITATION_REVOKED: m.error_invitation_revoked,
  INVITATION_ALREADY_ACCEPTED: m.error_invitation_already_accepted,
  LAST_ADMIN_PROTECTED: m.error_last_admin_protected,
  STALE_VERSION: m.error_stale_version,
  PERSON_INACTIVE: m.error_person_inactive,
  PERSON_HAS_RECORDS: m.error_person_has_records,
  SERVICE_UNAVAILABLE: m.error_service_unavailable,
};

/** The password policy answers with a reason code so the hint can say which rule was missed. */
const PASSWORD_REASONS: Record<string, () => string> = {
  TOO_SHORT: m.error_password_too_short,
  TOO_LONG: m.error_password_too_long,
  NOT_VARIED: m.error_password_not_varied,
  COMMON: m.error_password_not_varied,
  CONTEXTUAL: m.error_password_contextual,
};

const reasonDetails = z.object({ reason: z.string() });

function readReason(details: unknown, messages: Record<string, () => string>): string | null {
  const parsed = reasonDetails.safeParse(details);
  return parsed.success ? (messages[parsed.data.reason]?.() ?? null) : null;
}

const CREATION_REASONS: Record<string, () => string> = {
  DUPLICATE_CLIENT: m.person_create_duplicate_client,
  ASSIGNMENT_LIMIT: m.person_create_assignment_limit,
  INVALID_ASSIGNMENT_PERIOD: m.assignment_group_invalid_period,
  ASSIGNMENT_CLIENT_UNAVAILABLE: m.person_create_client_unavailable,
  ASSIGNMENT_PROJECT_UNAVAILABLE: m.person_create_project_unavailable,
};

function apiError<T>(
  body: ApiEnvelope<T> | null,
  path: string,
  method?: string,
): ReferenceDataError {
  const code = body && !body.success ? body.error.code : 'SERVICE_UNAVAILABLE';
  const details = body && !body.success ? body.error.details : undefined;
  const passwordError = readReason(details, PASSWORD_REASONS);
  const invitation = path === '/api/auth/invitation-acceptance';
  const reset = path === '/api/auth/password-resets';
  const message =
    passwordError ??
    (code === 'NOT_FOUND' && reset
      ? m.error_reset_link_invalid()
      : code === 'NOT_FOUND' && invitation
        ? m.error_link_or_record_missing()
        : code === 'PERSON_INACTIVE' && invitation
          ? m.api_person_inactive()
          : (MESSAGES[code]?.() ?? apiErrorMessage(code)));
  if (code === 'EMAIL_ALREADY_USED') {
    const message = invitation ? m.error_invitation_email_used() : m.error_email_already_used();
    return new ReferenceDataError(
      'VALIDATION',
      message,
      invitation ? {} : { intendedEmail: message },
    );
  }
  if (code === 'UNAUTHENTICATED' || code === 'FORBIDDEN')
    return new ReferenceDataError(code, message);
  if (code === 'NOT_FOUND') return new ReferenceDataError('NOT_FOUND', message);
  if (code === 'STALE_VERSION') return new ReferenceDataError('STALE', message);
  // LAST_ADMIN_PROTECTED and the invitation codes are all conditions the operator can act on,
  // so they surface as a stated reason rather than a generic failure.
  if (
    code === 'CONFLICT' ||
    code === 'VALIDATION_FAILED' ||
    code === 'BAD_REQUEST' ||
    code === 'LAST_ADMIN_PROTECTED' ||
    code === 'PERSON_INACTIVE' ||
    code === 'PERSON_HAS_RECORDS' ||
    code === 'INVALID_CREDENTIALS' ||
    code === 'THROTTLED' ||
    code.startsWith('INVITATION_')
  ) {
    const creating = path === '/api/users' && method === 'POST';
    const reason = creating ? readReason(details, CREATION_REASONS) : null;
    const fields = apiFieldErrors(
      details,
      {
        displayName: m.validation_name_length,
        emailHint: m.person_validation_email,
        email: m.person_validation_email,
        password: () => passwordError ?? m.validation_field(),
        intendedEmail: m.person_validation_email,
        role: m.person_validation_role,
        assignments: m.person_create_assignment_limit,
        groups: m.person_create_group_limit,
      },
      creating,
    );
    return new ReferenceDataError(
      'VALIDATION',
      creating && code === 'VALIDATION_FAILED' ? m.person_create_validation() : message,
      reason
        ? Object.fromEntries(Object.keys(fields).map((field) => [field, reason]))
        : passwordError
          ? { ...fields, password: message }
          : fields,
    );
  }
  return new ReferenceDataError('UNAVAILABLE', message);
}

type ApiEnvelope<T> =
  | { success: true; data: T; error: null }
  | {
      success: false;
      data: null;
      error: { code: string; message: string; details?: unknown };
    };
